/**
 * Einstellungen → Firma → Google-Bewertungen (sql/542).
 *
 * Link zur Google-Bewertungsseite mit Pruefung (dieselbe Regel wie der DB-CHECK), Vorschlag aus der
 * bestaetigten Place ID, Knopf „Link öffnen“ zum Selbsttest, dazu der Schalter, ob MAESTRO nach
 * Veranstaltungen eine Bewertungsbitte vorschlaegt. Liefert seine Werte ueber die Felder
 * `googleReviewUrl` und `bewertungsbitteAktiv` an das umgebende Formular.
 */
import { useState } from "react";
import { Button, ButtonLink, Input, Toggle } from "./components";

/** Wie GOOGLE_LINK_MUSTER in apps/api/src/lib/bewertungsbitte.ts und der CHECK in sql/542. */
const GOOGLE_LINK_MUSTER =
  /^https:\/\/(g\.page\/r\/[A-Za-z0-9_-]+(\/review)?\/?(\?[^\s<>"]*)?|search\.google\.com\/local\/writereview\?[^\s<>"]*placeid=[A-Za-z0-9_-]+[^\s<>"]*)$/;

export function googleLinkFehler(url: string): string | null {
  const u = url.trim();
  if (!u) return null;
  if (u.length > 500) return "Der Link ist zu lang (höchstens 500 Zeichen).";
  return GOOGLE_LINK_MUSTER.test(u)
    ? null
    : "Das ist kein Google-Bewertungslink. Erlaubt sind Links, die mit https://g.page/r/ oder https://search.google.com/local/writereview beginnen.";
}

export function linkAusPlaceId(placeId: string | null | undefined): string | null {
  const p = placeId?.trim().replace(/^places\//, "");
  return p && /^[A-Za-z0-9_-]{10,255}$/.test(p) ? `https://search.google.com/local/writereview?placeid=${p}` : null;
}

const KLEIN = "font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim";

export function GoogleBewertungEinstellung({
  googleReviewUrl,
  googlePlaceId,
  aktiv,
}: {
  googleReviewUrl: string | null | undefined;
  googlePlaceId: string | null | undefined;
  aktiv: boolean | null | undefined;
}) {
  const vorschlag = linkAusPlaceId(googlePlaceId);
  const [url, setUrl] = useState(googleReviewUrl ?? "");
  const [an, setAn] = useState(Boolean(aktiv));
  const fehler = googleLinkFehler(url);
  const wirksam = url.trim() ? (fehler ? null : url.trim()) : vorschlag;

  return (
    // min-w-0 + overflow-wrap:anywhere: lange Links (Fehlermeldung, Platzhalter) duerfen am Handy nie aus der Karte laufen.
    <div className="min-w-0 max-w-full space-y-3 rounded-xl border border-[#F0E7D6] px-4 py-4 [overflow-wrap:anywhere] dark:border-white/[0.06]" data-testid="google-bewertung-einstellung">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Nach Veranstaltungen eine Bewertungsbitte vorschlagen</p>
          <p className={KLEIN}>
            Drei Werktage nach der Veranstaltung erscheint ein Vorschlag unter „Freigaben“. Verschickt wird erst nach Ihrer Freigabe und nur an Gäste, die bei der Annahme oder im Kundenportal zugestimmt haben.
          </p>
        </div>
        <Toggle checked={an} onChange={setAn} label="Bewertungsbitte vorschlagen" />
        <input type="hidden" name="bewertungsbitteAktiv" value={an ? "true" : "false"} />
      </div>

      <label htmlFor="googleReviewUrl" className="block font-label-md text-label-md text-on-background dark:text-inverse-on-surface">
        Google-Bewertungslink
      </label>
      <Input
        id="googleReviewUrl"
        name="googleReviewUrl"
        className="w-full min-w-0 max-w-full"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder={vorschlag ?? "https://g.page/r/…/review"}
        inputMode="url"
        autoComplete="off"
        aria-invalid={fehler ? true : undefined}
      />
      {fehler ? (
        <p className="font-body-sm text-body-sm font-semibold text-error" data-testid="google-link-fehler">{fehler}</p>
      ) : url.trim() ? (
        <p className="font-body-sm text-body-sm font-semibold text-[#6F675A] dark:text-[#b8ae9e]">✓ Gültiger Google-Bewertungslink.</p>
      ) : vorschlag ? (
        <p className="font-body-sm text-body-sm font-semibold text-[#6F675A] dark:text-[#b8ae9e]">
          ✓ Aus Ihrem bestätigten Google-Eintrag übernommen. Sie können ihn durch den Kurzlink aus Ihrem Unternehmensprofil ersetzen („Mehr Rezensionen erhalten“ → „Kopieren“).
        </p>
      ) : (
        <p className={KLEIN}>
          Kopieren Sie den Link aus Ihrem Google-Unternehmensprofil: „Rezensionen lesen“ → „Mehr Rezensionen erhalten“ → „Kopieren“. Ohne Link entstehen keine Vorschläge.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {wirksam ? (
          <ButtonLink variant="secondary" href={wirksam} target="_blank" rel="noopener noreferrer" data-testid="google-link-oeffnen">
            Link öffnen
          </ButtonLink>
        ) : (
          <Button type="button" variant="secondary" disabled>
            Link öffnen
          </Button>
        )}
        {vorschlag && url.trim() && (
          <Button type="button" variant="secondary" onClick={() => setUrl("")}>
            Vorschlag aus Google-Eintrag verwenden
          </Button>
        )}
      </div>
    </div>
  );
}
